<script lang="ts">
  import SocialMediaGenerator from './SocialMediaGenerator.svelte';
  import ButtonSwitch from './ButtonSwitch.svelte';
  import type { Match, SessionInfo } from '../types';
  import { _ } from 'svelte-i18n';
  import { showToast } from '../lib/toast';
  import { onMount, onDestroy } from 'svelte';
  import { getSessionInfo, trackEvent, trackMatchShare } from '../lib/apiService';
  import { formatMatchDate, getTeamData, getWeatherEmoji, roundToNearestHour, getGoogleMapsUrl } from '../lib/utils/shareUtils';
  export let report: Match;
  export let type: 'announcement' | 'report';
  export let onClose: () => void;
  export let sessionInfo: SessionInfo;

  import { createEventDispatcher } from 'svelte';
  const dispatch = createEventDispatcher();

  // Layout selection for match report
  let reportLayout: 'portrait' | 'landscape' = 'portrait';
  $: layoutOptions = [
    { value: 'portrait', label: $_('shareView.layoutPortrait'), size: 'small' },
    { value: 'landscape', label: $_('shareView.layoutLandscape'), size: 'small' }
  ];

  function handleChange(event) {
    dispatch('change', event.detail);
  }
  
  // Get the image URL's
  // TOOD: This is a bit weird and has an unnneceassary dependency on club. We link the default image to the sport of the club. Is highly likely not to change, but better to store the photo more independent of the club properties
  $: announcementImageUrl = report.announcement_photo_filename
    ? (report.announcement_photo_url 
       ? report.announcement_photo_url
       : (report.announcement_photo_filename.includes('/')
          ? `/uploads/${report.announcement_photo_filename}`
          : `/img/backgrounds/announcement/${sessionInfo.active_team.club.sport}/${report.announcement_photo_filename}`))
    : `/img/backgrounds/announcement/${sessionInfo.active_team.club.sport}/background_1.jpg`;

  $: reportImageUrl = report.match_photo_filename
    ? (report.match_photo_url
       ? report.match_photo_url
       : (report.match_photo_filename.includes('/')
          ? `/uploads/${report.match_photo_filename}`
          : `/img/backgrounds/match/${sessionInfo.active_team.club.sport}/${report.match_photo_filename}`))
    : `/img/backgrounds/match/${sessionInfo.active_team.club.sport}/background_1.jpg`;

  $: awardImageUrls = report.player_awards ? 
    report.player_awards.reduce((acc, award) => {
      acc[award.id] = award.award_photo_filename
        ? (award.award_photo_url
           ? award.award_photo_url
           : (award.award_photo_filename.includes('/')
              ? `/uploads/${award.award_photo_filename}`
              : `/img/backgrounds/award/${sessionInfo.active_team.club.sport}/${award.award_photo_filename}`))
        : `/img/backgrounds/award/${sessionInfo.active_team.club.sport}/background_1.jpg`;
      return acc;
    }, {}) : {};

  $: activeTab = type === 'announcement' ? 'announcement_image' : 'report_image';
  let announcementText = '';
  let reportText = '';
  $: if (activeTab === 'announcement_image') {
    trackEvent('select_share_tab', {
      module: "share_view",
      action_type: "select_view",
      description: 'Select share tab',
      value: activeTab
    }, sessionInfo.user.id);
  }
  $: if (activeTab === 'report_image') {
    trackEvent('select_share_tab', {
      module: "share_view",
      action_type: "select_view",
      description: 'Select share tab',
      value: activeTab
    }, sessionInfo.user.id);
  }
  $: if (activeTab === 'awards') {
    trackEvent('select_share_tab', {
      module: "share_view",
      action_type: "select_view",
      description: 'Select share tab',
      value: activeTab
    }, sessionInfo.user.id);
  }
  $: if (activeTab === 'announcement_text') {
    buildAnnouncementText().then(text => announcementText = text);
    trackEvent('select_share_tab', {
      module: "share_view",
      action_type: "select_view",
      description: 'Select share tab',
      value: activeTab
    }, sessionInfo.user.id);
  }
  $: if (activeTab === 'report_text') {
    buildReportText().then(text => reportText = text);
    trackEvent('select_share_tab', {
      module: "share_view",
      action_type: "select_view",
      description: 'Select share tab',
      value: activeTab
    }, sessionInfo.user.id);
  }

  function isShareAvailable() {
    return navigator.share && navigator.canShare;
  }

  async function buildAnnouncementText(): Promise<string> {
    const match_date = new Date(report.match_date);
    const match_date_string = formatMatchDate(match_date);
    const team_data = getTeamData(report, sessionInfo, (report.location.toLowerCase() === 'away'));
    const time = report.match_date.split('T')[1].slice(0, 5); // Gets HH:MM from the datetime string        

    let weather_text = '';
    let temp = '';
    let weatherEmoji = '';
    let forecast = null;
    let weather_info = '';
    try {
    const weatherApiKey = import.meta.env.VITE_WEATHER_API_KEY; // Add this to your .env file
    const weatherResponse = await fetch(`https://api.weatherapi.com/v1/forecast.json?key=${weatherApiKey}&q=${report.opponent_club?.location_name},${report.opponent_club?.location_address},${report.opponent_club?.location_postal_code},${report.opponent_club?.location_city},${report.opponent_club?.location_country}&days=14&aqi=no&alerts=no`);
    const weatherData = await weatherResponse.json();
    forecast = weatherData.forecast.forecastday
      .find(day => day.date === match_date.toISOString().split('T')[0])
      ?.hour
      .find(hour => {
        const matchDateRounded = roundToNearestHour(match_date);
        const searchDateTime = `${matchDateRounded.getFullYear()}-${String(matchDateRounded.getMonth() + 1).padStart(2, '0')}-${String(matchDateRounded.getDate()).padStart(2, '0')} ${String(matchDateRounded.getHours()).padStart(2, '0')}:00`;
        return hour.time === searchDateTime;
      });
    } catch (error) {
      console.error('Error fetching weather data:', error);
    }
    if (forecast) {
      weather_text = forecast.condition.text;
      temp = Math.round(forecast.temp_c).toString();
      weatherEmoji = getWeatherEmoji(weather_text);
      weather_info = `${weatherEmoji} ${weather_text} ${temp}°\n`;
    }

    const basic_info = `${team_data.home.club} ${team_data.home.name} vs ${team_data.away.club} ${team_data.away.name}\n${match_date_string}, ${time}\n${team_data.home.location_name}\n`;
    let location_info = '';
    if (team_data.home.location_address && team_data.home.location_city) {
      location_info = `${String(team_data.home.location_address)}, ${String(team_data.home.location_zip_code)}, ${String(team_data.home.location_city)}\n`;
    }
    let meeting_info = `⏰${$_('socialMediaGenerator.meeting_label')}:\n${report.meeting_time} ${report.meeting_location || ''}`;
    if (report.location.toLowerCase() === 'away') {
      meeting_info += `\n\n🚘 ${$_('socialMediaGenerator.directions_label')}:\n${getGoogleMapsUrl(team_data.home.location_address || "", team_data.home.location_city || "", team_data.home.location_zip_code || "")}`;
    }

    let task_info = '';
    if (report.task_list) {
      task_info = `\n\n${report.task_list.map(task => `${task.icon ? task.icon : ''} ${task.label}: ${task.user_name}`).join('\n')}`;
    }
    
    return `${basic_info}${location_info}${weather_info}\n${meeting_info}${task_info}`;

  }

  async function buildReportText(): Promise<string> {
    let title = `${sessionInfo.active_team.name} vs ${report.opponent_club?.name}`;
    let score = `${report.location.toLowerCase() === 'away' ? report.score_opponent : report.score_team} - ${report.location.toLowerCase() === 'away' ? report.score_team : report.score_opponent}`;
    let text = `${title}\n${score}\n\n${report.match_report}`;
    return text;
  }

  async function shareTextReport() {
    try {

      let title, score, text = "";
      if (type === 'announcement') {
        text = announcementText;
      } else {
        text = reportText;
      }

      if (isShareAvailable()) {
        trackEvent('share_text', {
          module: "settings",
          action_type: "create_team",
          description: 'Create team',
          value: type
        }, sessionInfo.user.id);
        await navigator.share({
          title: title,
          text: text
        });

        // Track share to backend
        trackShareToBackend();
      } else {
        // Fallback for browsers that don't support sharing
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        showToast($_('shareView.copiedToClipboard'), 'success');

        // Track copy as a share
        trackShareToBackend();
      }
    } catch (error) {
      console.error('Error sharing text report:', error);
      showToast($_('shareView.errorSharing'), 'error');
    }
  }

  function trackShareToBackend() {
    if (!report || !report.id) return;

    // Determine share type based on type prop
    const shareType = type === 'announcement' ? 'announcement' : 'report';

    // Call API to track share
    trackMatchShare(report.id, shareType, (result, status) => {
      if (status === 200) {
        console.log('Text share tracked successfully', result);
      } else {
        console.warn('Failed to track text share', status);
      }
    });
  }
  

  // Add body class when component mounts
  onMount(async () => {
    await getSessionInfo((result, status) => {
      if (status === 200) {
        sessionInfo = result;
      }
    });
    document.body.classList.add('share-view-open');

  
  });

  // Remove body class when component unmounts
  onDestroy(() => {
    document.body.classList.remove('share-view-open');
  });
</script>

<dialog open class="full-screen">
  <article class="share-content">
    <div class="sticky-header">
      <nav class="tabs">
        <ul>
          {#if type === 'announcement'}
          <li>
            <button  
              class="tab-button" 
              class:active={activeTab === 'announcement_image'}
              on:click={() => activeTab = 'announcement_image'}
            >
              {$_('shareView.announcementImageTab')}
            </button>
          </li>
          <li>
              <button  
                class="tab-button" 
                class:active={activeTab === 'announcement_text'}
                on:click={() => activeTab = 'announcement_text'}
                >
                  {$_('shareView.announcementTextTab')}
              </button>
            </li>
          {/if}
          {#if type === 'report'}
            <li>
              <button  
                class="tab-button" 
                class:active={activeTab === 'report_image'}
                on:click={() => activeTab = 'report_image'}
              >
                {$_('shareView.reportImageTab')}
              </button>
            </li>
            <li>
              <button  
                class="tab-button" 
                class:active={activeTab === 'report_text'}
                on:click={() => activeTab = 'report_text'}
                >
                  {$_('shareView.reportTextTab')}
              </button>
            </li>
            <li>
              <button  
                class="tab-button" 
                class:active={activeTab === 'awards'}
                on:click={() => activeTab = 'awards'}
                >
                {$_('shareView.awardsTab')}
              </button>
            </li>
          {/if}
        </ul>
      </nav>
      <button class="close-button" on:click={onClose}>×</button>
    </div>

    <!-- Tab content -->
    {#if activeTab === 'announcement_image'}
        <SocialMediaGenerator {report} reportImage={announcementImageUrl} cardType="match_announcement" on:change={handleChange} />
    {:else if activeTab === 'report_image'}
        <!-- Layout selector for match report -->
        <div class="layout-selector">
          <ButtonSwitch bind:selected_value={reportLayout} options={layoutOptions} />
        </div>
        <SocialMediaGenerator {report} reportImage={reportImageUrl} cardType="match_report" layout={reportLayout} on:change={handleChange} />
    {:else if activeTab === 'announcement_text'}
      <div class="report-content">
        <textarea class="report-text-area" bind:value={announcementText}></textarea>
      </div>
      <div class="sticky-actions">
        <p class="text text-sm text-italic"><ion-icon class="text text-primary" name="warning-outline"></ion-icon> {$_('shareView.warning')}</p>
        <button class="success full-width share-button" on:click={shareTextReport}>
          <ion-icon name="share-outline"></ion-icon>
          {isShareAvailable() ? $_('shareView.shareButton') : $_('shareView.downloadButton')}
        </button>
      </div>
    {:else if activeTab === 'report_text'}
      <div class="report-content">
        <textarea class="report-text-area" bind:value={reportText}></textarea>
      </div>
      <div class="sticky-actions">
        <button class="success full-width share-button" on:click={shareTextReport}>
          <ion-icon name="share-outline"></ion-icon>
          {isShareAvailable() ? $_('shareView.shareButton') : $_('shareView.downloadButton')}
        </button>
      </div>
    {/if}
    {#if activeTab === 'awards'}
      <SocialMediaGenerator {report} reportImages={awardImageUrls} cardType="player_award" on:change={handleChange} />
    {/if}
    
  </article>
</dialog>

<style>

  article.share-content {
    margin: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 1rem !important;
    flex: 1;
    max-width: 800px;
    width: 100%;
    display: flex;
    flex-direction: column;
    height: 100%;
    align-self: center;
    max-height: 100%;
  }

  .sticky-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0;
    margin: 0;
    background-color: var(--pico-card-background-color);
    position: sticky;
    top: 0;
    z-index: 1001;
    margin-bottom: 1rem;
  }

  .sticky-header .tabs {
    flex: 1;
    margin: 0;
    margin-top: 1rem;
    margin-bottom: 1rem;
  }

  .sticky-header .tabs ul {
    display: flex;
    justify-content: center;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .sticky-header .tabs li {
    margin: 0;
    padding: 0;
  }

  .sticky-header .tab-button {
    background: transparent;
    border: none;
    padding: 0.75rem 1rem;
    /* font-size: 0.875rem; */
    color: var(--pico-muted-color);
    cursor: pointer;
    margin: 0;
    white-space: nowrap;
    border-bottom: 2px solid transparent;
    font-size: 1rem;
  }

  .sticky-header .tab-button.active {
    color: var(--pico-primary-text);
    border-bottom: 2px solid var(--pico-primary);
    font-weight: 500;
  }

  .close-button {
    background: none;
    border: none;
    font-size: 2rem;
    line-height: 1;
    padding: 0.5rem;
    cursor: pointer;
    color: var(--pico-primary-text);
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 0.5rem 0 0;
    border-radius: 50%;
  }

  .close-button:hover {
    cursor: pointer;
  }

  .report-content {
    flex: 1;
    overflow-y: auto;
    padding-bottom: 40px; /* Space for the sticky button */
  }

  .report-text-area {
   height: 100%;
   margin:0;
  }

  .layout-selector {
    width: 100%;
    margin-bottom: 0rem;
  }
</style> 